import {
	AsYouType,
	getCountries,
	getCountryCallingCode,
	isValidPhoneNumber,
	parsePhoneNumberFromString,
} from 'libphonenumber-js/min';

export const DEFAULT_COUNTRY = 'BR';
export const DEFAULT_COUNTRY_CODE = '55';

// Países que dividem o mesmo DDI: sem outra informação, usamos o principal
const MAIN_COUNTRY_BY_CALLING_CODE = {
	1: 'US',
	7: 'RU',
	39: 'IT',
	44: 'GB',
	47: 'NO',
	61: 'AU',
	212: 'MA',
	262: 'RE',
	290: 'SH',
	358: 'FI',
	590: 'GP',
	599: 'CW',
};

export const onlyDigits = (value) => (value === null || value === undefined ? '' : String(value).replace(/\D/g, ''));

export const getCallingCode = (country) => {
	try {
		return getCountryCallingCode(country || DEFAULT_COUNTRY);
	} catch (e) {
		return DEFAULT_COUNTRY_CODE;
	}
};

export const getCountryName = (() => {
	let displayNames = null;

	try {
		displayNames = new Intl.DisplayNames(['pt-BR'], { type: 'region' });
	} catch (e) {
		displayNames = null;
	}

	return (country) => {
		try {
			return displayNames?.of(country) || country;
		} catch (e) {
			return country;
		}
	};
})();

// Lista de países para o seletor, ordenada pelo nome em português (Brasil primeiro)
let countryOptionsCache = null;

export const getCountryOptions = () => {
	if (countryOptionsCache) return countryOptionsCache;

	const options = getCountries().map((country) => ({
		country,
		name: getCountryName(country),
		callingCode: getCallingCode(country),
	}));

	options.sort((a, b) => {
		if (a.country === DEFAULT_COUNTRY) return -1;
		if (b.country === DEFAULT_COUNTRY) return 1;
		return a.name.localeCompare(b.name, 'pt-BR');
	});

	countryOptionsCache = options;
	return options;
};

// Resolve o país (ISO) a partir do DDI salvo no backend e, se houver, do número
export const resolveCountry = ({ country, countryCode, phone } = {}) => {
	if (country) return country;

	const code = onlyDigits(countryCode) || DEFAULT_COUNTRY_CODE;

	if (code === DEFAULT_COUNTRY_CODE) return DEFAULT_COUNTRY;

	const parsed = phone ? parsePhoneNumberFromString(`+${code}${onlyDigits(phone)}`) : null;

	if (parsed?.country) return parsed.country;

	if (MAIN_COUNTRY_BY_CALLING_CODE[code]) return MAIN_COUNTRY_BY_CALLING_CODE[code];

	return getCountries().find((item) => getCallingCode(item) === code) || DEFAULT_COUNTRY;
};

// Número nacional formatado conforme o país (ex: "(41) 99999-8888", "912 345 678")
export const formatNationalPhone = (country, phone) => {
	const digits = onlyDigits(phone);

	if (!digits) return '';

	return new AsYouType(country || DEFAULT_COUNTRY).input(digits);
};

// Brasil segue a regra das telas (padrão: celular com DDD, 11 dígitos; `brazilianLengths` permite fixo);
// demais países usam a libphonenumber
export const isValidPhone = ({ country, countryCode, phone, brazilianLengths = [11] } = {}) => {
	const digits = onlyDigits(phone);
	const resolvedCountry = resolveCountry({ country, countryCode, phone });

	if (!digits) return false;

	if (resolvedCountry === DEFAULT_COUNTRY) {
		return brazilianLengths.includes(digits.length);
	}

	return isValidPhoneNumber(digits, resolvedCountry);
};

export const isBrazilianPhone = ({ country, countryCode } = {}) => resolveCountry({ country, countryCode }) === DEFAULT_COUNTRY;

// Número completo no formato internacional (ex: +351912345678)
export const toE164 = ({ countryCode, phone } = {}) => `+${onlyDigits(countryCode) || DEFAULT_COUNTRY_CODE}${onlyDigits(phone)}`;

// Formata para exibição: Brasil na máscara nacional, demais países com o DDI na frente
export const formatPhoneDisplay = ({ countryCode, phone, country } = {}) => {
	const digits = onlyDigits(phone);

	if (!digits) return '';

	const code = onlyDigits(countryCode) || DEFAULT_COUNTRY_CODE;
	const resolvedCountry = resolveCountry({ country, countryCode: code, phone: digits });
	const national = formatNationalPhone(resolvedCountry, digits);

	return code === DEFAULT_COUNTRY_CODE ? national : `+${code} ${national}`;
};

export const PHONE_REQUIRED_MESSAGE = 'Informe um número de telefone.';
export const PHONE_DUPLICATED_MESSAGE = 'Já existe um cadastro com esse telefone.';

// Mensagem de erro do campo de telefone (null quando válido). `brazilianLengths` segue a regra da tela.
export const getPhoneValidationError = ({ country, countryCode, phone, brazilianLengths = [11] } = {}) => {
	const digits = onlyDigits(phone);

	if (!digits) return PHONE_REQUIRED_MESSAGE;

	if (isValidPhone({ country, countryCode, phone: digits, brazilianLengths })) return null;

	const resolvedCountry = resolveCountry({ country, countryCode, phone: digits });

	if (resolvedCountry === DEFAULT_COUNTRY) {
		return digits.length < Math.max(...brazilianLengths)
			? 'Número incompleto. Um celular brasileiro tem 11 dígitos com o DDD.'
			: 'Esse número não é válido para o Brasil. Confira o país selecionado.';
	}

	return `Esse número não é válido para ${getCountryName(resolvedCountry)}. Confira o país selecionado.`;
};
